import {Injectable} from '@angular/core';
import {Container, Image, ImageBox, Tab} from '../model/layout.model';
import {DocumentModel} from '../model/document.model';

/** Was die Übernahme bewirkt hat – Grundlage für die Rückmeldung an den Redakteur. */
export interface UebernahmeErgebnis {
  /** COSMO-Karten, in die etwas geschrieben wurde. */
  karten: number;
  /** COSMO-Boxen, die einen Wert aus NRW bekommen haben. */
  boxen: number;
  /** Einzelne Werte (Temperatur, Symbol, Wind, Böen, Richtung, Warnschild), die geschrieben wurden. */
  werte: number;
  /** `containerId`s der geänderten COSMO-Karten – die müssen ihre Anzeige neu aufbauen. */
  kartenIds: string[];
  /** Zeilen, für die es in COSMO keine Entsprechung gibt (COSMO hat weniger Zeitfenster). */
  uebersprungeneZeilen: string[];
  /** Klartext, warum nichts übernommen wurde – nur gesetzt, wenn `werte === 0`. */
  grund?: string;
}

/** Ein fachlicher Wert einer Box, unabhängig davon, unter welchem Schlüssel er liegt. */
type Feld = 'temp' | 'icon' | 'wind' | 'boeen' | 'dir' | 'warnschild';

/**
 * Was NRW über ein Gebiet weiß: je Feld der Wert – oder `null`, wenn eine NRW-Box dieses
 * Feld führt, es aber leer ist (dann wird es auch in COSMO geleert). Dazu die Umschalter,
 * geschlüsselt über {@link CosmoTakeoverService.umschalterSchluessel}: die der Wind-Box
 * getrennt von denen der Symbol-Boxen, weil "NRW ganz" in NRW ein Symbol (Karte 1) **und**
 * eine Wind-Box (Karte 7) ist – der gelbe Haken am Wind darf nicht das COSMO-Symbol anhaken.
 */
interface GebietsWerte {
  felder: Map<Feld, string | null>;
  umschalter: Map<string, boolean>;
}

/**
 * Überträgt die Grafik-Werte eines Nordrhein-Westfalen-Reiters nach COSMO – so, wie sie
 * gerade in den Karten stehen, auch von Hand geänderte.
 *
 * <b>Zugeordnet wird über den Regionsnamen, nicht über die Box.</b> COSMO hat eigene Karten
 * mit eigenen Box-Ids (`c1_1`, `c4_3` …), NRW andere (`temp`, `f_1`, `w_1` …), und sogar
 * andere Box-Typen für dasselbe Gebiet ("NRW ganz" ist in NRW ein Symbol, in COSMO eine
 * Temperatur-Symbol-Box). Die frühere Übernahme kopierte `image.values` Karte für Karte mit
 * den NRW-Schlüsseln – die fand in COSMO keine Box, es kam also nichts an, und die
 * Rückmeldung zählte trotzdem die Quell-Werte und meldete grün.
 *
 * Jetzt wird jede Zeile in fachliche Werte je Gebiet zerlegt (`temp`, `icon`, `wind`,
 * `boeen`, `dir`, `warnschild` plus die Umschalter `ai`/`warn`) und in **jede** COSMO-Box
 * derselben Zeile mit **genau** demselben Regionsnamen geschrieben – unter den Schlüsseln,
 * die zu deren Typ gehören. Kein Ähnlichkeitsvergleich: "Sauerland" ist nicht
 * "Hochsauerland". Was NRW nicht kennt, bleibt in COSMO unberührt (Bremen, Berlin, und
 * Felder, die die NRW-Box nicht führt – etwa die Temperatur zu "Rheinland", das in NRW nur
 * ein Symbol hat). Ein Feld, das NRW führt, aber leer hat, wird auch in COSMO geleert.
 *
 * Boxen **ohne** Region (das Warnschild `wert` jeder Karte) haben keinen Namen zum
 * Vergleichen; sie gehen über die Kartenzuordnung, die die COSMO-Zeilen aufgebaut hat
 * ({@link KARTEN_ZUORDNUNG}), und dieselbe Box-Id.
 *
 * Geschrieben wird in `image.values` der COSMO-Karten. Noch nie geöffnete Reiter lesen das
 * beim Öffnen; schon gerenderte Karten müssen ihre Anzeige neu aufbauen – deshalb liefert
 * das Ergebnis die geänderten `kartenIds`.
 */
@Injectable({providedIn: 'root'})
export class CosmoTakeoverService {

  /**
   * NRW-Kartennummer -> COSMO-Kartennummer, nur noch für Boxen **ohne** Region (das
   * Warnschild einer Karte). So wurden die COSMO-Zeilen aufgebaut.
   */
  private static readonly KARTEN_ZUORDNUNG = new Map<number, number>([
    [1, 1],   // NRW ganz            -> ganze Region
    [2, 2],   // Rheinland/Westfalen -> Einzelregionen
    [3, 3],   // Nord-/Südhälfte     -> NRW West-Ost / Nord-Süd
    [5, 4],   // feine Unterteilung  -> NRW feine Unterteilung
    [7, 5],   // Wind in Bft.        -> Windböen in Bft.
  ]);

  /** Trennzeichen zwischen Box und Umschalter (wie im `ImageComponent`). */
  private static readonly UMSCHALTER_TRENNER = '::';

  /**
   * Schlüssel eines Umschalters in {@link GebietsWerte.umschalter}: der Umschalter einer
   * Wind-Box zählt nur für Wind-Boxen, der aller anderen Boxen nur für die anderen.
   */
  private static umschalterSchluessel(box: ImageBox, umschalterId: string): string {
    return (box.type === 'wind' ? 'wind:' : '') + umschalterId;
  }

  /**
   * @param document das Dokument, so wie es auf der Seite steht
   * @param reiter   Beschriftung des offenen NRW-Reiters, z.B. "Heute"
   * @param zeile    Beschriftung **einer** Zeile, oder null für alle Zeilen des Reiters.
   *   Gesetzt vom Knopf an einer Zeilenüberschrift, null vom Knopf der Kopfleiste. Der
   *   Name genügt hier, weil er innerhalb eines Reiters eindeutig ist – und weil die
   *   Übernahme ohnehin über den Namen die Ziel-Zeile in COSMO sucht.
   */
  public uebernehmen(document: DocumentModel, reiter: string,
                     zeile: string | null = null): UebernahmeErgebnis {
    const ergebnis: UebernahmeErgebnis =
      {karten: 0, boxen: 0, werte: 0, kartenIds: [], uebersprungeneZeilen: []};

    const nrwReiter = this.findeReiter(document, 'Nordrhein-Westfalen', reiter);
    const cosmoReiter = this.findeReiter(document, 'COSMO', reiter);
    if (!nrwReiter) {
      ergebnis.grund = `Der Reiter "${reiter}" wurde unter Nordrhein-Westfalen nicht gefunden.`;
      return ergebnis;
    }
    if (!cosmoReiter) {
      ergebnis.grund = `COSMO hat keinen Reiter "${reiter}".`;
      return ergebnis;
    }

    const quellZeilen = (nrwReiter.containerList ?? [])
      .filter(z => !zeile || z.caption === zeile);
    if (zeile && quellZeilen.length === 0) {
      ergebnis.grund = `Die Zeile "${zeile}" wurde im Reiter "${reiter}" nicht gefunden.`;
      return ergebnis;
    }

    // Ist in NRW nichts ausgefüllt, wird auch nichts geleert: ein Klick auf einen leeren
    // Reiter soll COSMO nicht stillschweigend ausradieren.
    // Gezählt werden nur Zeilen, die COSMO auch führt.
    const mitWerten = quellZeilen.some(z =>
      (cosmoReiter.containerList ?? []).some(c => c.caption === z.caption)
      && this.karten(z).some(k => this.hatWerte(k)));

    let passendeBoxen = 0;
    const geaendert = new Set<Image>();
    for (const quellZeile of quellZeilen) {
      const zielZeile = (cosmoReiter.containerList ?? [])
        .find(z => z.caption === quellZeile.caption);
      if (!zielZeile) {
        if (this.karten(quellZeile).length > 0) {
          ergebnis.uebersprungeneZeilen.push(quellZeile.caption ?? '');
        }
        continue;
      }
      if (!mitWerten) {
        continue;
      }
      const gebiete = this.gebieteVon(quellZeile);
      for (const ziel of this.karten(zielZeile)) {
        const ohneRegion = this.ohneRegionQuelle(quellZeile, ziel);
        for (const box of ziel.boxes ?? []) {
          const quelle = box.region ? gebiete.get(box.region) : ohneRegion?.get(box.id);
          if (!quelle) {
            continue;
          }
          passendeBoxen++;
          const geschrieben = this.schreibe(ziel, box, quelle);
          if (geschrieben.geaendert) {
            geaendert.add(ziel);
          }
          if (geschrieben.werte > 0) {
            ergebnis.boxen++;
            ergebnis.werte += geschrieben.werte;
          }
        }
      }
    }

    ergebnis.karten = geaendert.size;
    ergebnis.kartenIds = [...geaendert].map(k => k.containerId).filter(id => !!id);
    if (!mitWerten) {
      ergebnis.grund = zeile
        ? `In der Zeile "${zeile}" ist in Nordrhein-Westfalen nichts ausgefüllt.`
        : `Im Reiter "${reiter}" ist in Nordrhein-Westfalen nichts ausgefüllt.`;
    } else if (ergebnis.werte === 0) {
      ergebnis.grund = passendeBoxen === 0
        ? (zeile
          ? `Die Zeile "${zeile}" hat keine Boxen, die COSMO auch führt.`
          : `Im Reiter "${reiter}" gibt es keine Boxen, die COSMO auch führt.`)
        : (zeile
          ? `In der Zeile "${zeile}" ist in Nordrhein-Westfalen nichts ausgefüllt.`
          : `Im Reiter "${reiter}" ist in Nordrhein-Westfalen nichts ausgefüllt.`);
    }
    return ergebnis;
  }

  /**
   * Die fachlichen Werte je Regionsname über alle Karten einer NRW-Zeile. Dasselbe Gebiet
   * kann auf mehreren Karten stehen (Temperatur-Karte und Wind-Karte führen beide
   * "Niederrhein"); die Felder ergänzen sich dann. Führen zwei Boxen dasselbe Feld, gewinnt
   * der erste gefüllte Wert.
   */
  private gebieteVon(zeile: Container): Map<string, GebietsWerte> {
    const gebiete = new Map<string, GebietsWerte>();
    for (const karte of this.karten(zeile)) {
      for (const box of karte.boxes ?? []) {
        if (box.region) {
          const gebiet = gebiete.get(box.region) ?? {felder: new Map(), umschalter: new Map()};
          this.ergaenze(gebiet, karte, box);
          gebiete.set(box.region, gebiet);
        }
      }
    }
    return gebiete;
  }

  /**
   * Die Boxen ohne Region der NRW-Karte, die dieser COSMO-Karte entspricht – geschlüsselt
   * über die Box-Id. Null, wenn es keine entsprechende Karte gibt.
   */
  private ohneRegionQuelle(quellZeile: Container, ziel: Image): Map<string, GebietsWerte> | null {
    const zielNummer = this.kartenNummer(ziel);
    const quellNummer = [...CosmoTakeoverService.KARTEN_ZUORDNUNG.entries()]
      .find(([, cosmo]) => cosmo === zielNummer)?.[0];
    const quelle = this.karten(quellZeile).find(k => this.kartenNummer(k) === quellNummer);
    if (!quelle) {
      return null;
    }
    const boxen = new Map<string, GebietsWerte>();
    for (const box of quelle.boxes ?? []) {
      if (!box.region) {
        const werte: GebietsWerte = {felder: new Map(), umschalter: new Map()};
        this.ergaenze(werte, quelle, box);
        boxen.set(box.id, werte);
      }
    }
    return boxen;
  }

  /** Trägt die Felder und Umschalter einer NRW-Box in `ziel` ein. */
  private ergaenze(ziel: GebietsWerte, karte: Image, box: ImageBox): void {
    const werte = karte.values ?? {};
    for (const [feld, schluessel] of CosmoTakeoverService.felderVon(box)) {
      const wert = CosmoTakeoverService.istWert(werte[schluessel]) ? werte[schluessel] : null;
      if (!ziel.felder.has(feld) || (ziel.felder.get(feld) === null && wert !== null)) {
        ziel.felder.set(feld, wert);
      }
    }
    for (const umschalter of box.toggles ?? []) {
      const gesetzt = werte[box.id + CosmoTakeoverService.UMSCHALTER_TRENNER + umschalter.id] === '1';
      const schluessel = CosmoTakeoverService.umschalterSchluessel(box, umschalter.id);
      ziel.umschalter.set(schluessel, (ziel.umschalter.get(schluessel) ?? false) || gesetzt);
    }
  }

  /**
   * Schreibt die NRW-Werte eines Gebiets in eine COSMO-Box. Nur die Felder, die die
   * COSMO-Box führt **und** NRW kennt; ein leeres NRW-Feld leert auch das COSMO-Feld.
   */
  private schreibe(karte: Image, box: ImageBox, quelle: GebietsWerte): {werte: number; geaendert: boolean} {
    const werte = {...(karte.values ?? {})};
    let geschrieben = 0;
    let geaendert = false;
    const setze = (schluessel: string, wert: string | null) => {
      if (wert === null) {
        if (schluessel in werte) {
          delete werte[schluessel];
          geaendert = true;
        }
        return;
      }
      if (werte[schluessel] !== wert) {
        werte[schluessel] = wert;
        geaendert = true;
      }
      if (!schluessel.includes(CosmoTakeoverService.UMSCHALTER_TRENNER)) {
        geschrieben++;
      }
    };
    for (const [feld, schluessel] of CosmoTakeoverService.felderVon(box)) {
      if (quelle.felder.has(feld)) {
        setze(schluessel, quelle.felder.get(feld)!);
      }
    }
    for (const umschalter of box.toggles ?? []) {
      const schluessel = CosmoTakeoverService.umschalterSchluessel(box, umschalter.id);
      if (quelle.umschalter.has(schluessel)) {
        setze(box.id + CosmoTakeoverService.UMSCHALTER_TRENNER + umschalter.id,
          quelle.umschalter.get(schluessel) ? '1' : null);
      }
    }
    if (geaendert) {
      karte.values = werte;
    }
    return {werte: geschrieben, geaendert};
  }

  /**
   * Welche fachlichen Felder eine Box führt und unter welchem Schlüssel sie in
   * `image.values` liegen – dieselben Schlüssel wie im `ImageComponent`.
   */
  private static felderVon(box: ImageBox): [Feld, string][] {
    switch (box.type) {
      case 'icon':
        return [['icon', box.id]];
      case 'tempicon':
        return [['temp', box.id], ['icon', box.id + '.icon']];
      case 'temperature':
        return [['temp', box.id]];
      case 'wind':
        return [['wind', box.id], ['boeen', box.id + '.boeen'], ['dir', box.id + '.dir']];
      case 'danger':
        return [['warnschild', box.id]];
      default:
        return [];
    }
  }

  /** Trägt eine NRW-Karte irgendeinen Box-Wert oder gesetzten Umschalter? */
  private hatWerte(karte: Image): boolean {
    const werte = karte.values ?? {};
    return (karte.boxes ?? []).some(box =>
      CosmoTakeoverService.felderVon(box).some(([, schluessel]) => CosmoTakeoverService.istWert(werte[schluessel]))
      || (box.toggles ?? []).some(t => werte[box.id + CosmoTakeoverService.UMSCHALTER_TRENNER + t.id] === '1'));
  }

  /** Wie `ImageComponent.istWert`: `null` vom Server ist kein Wert, ein Leerstring auch nicht. */
  private static istWert(wert: string | null | undefined): wert is string {
    return wert !== undefined && wert !== null && wert !== '';
  }

  private findeReiter(document: DocumentModel, bereich: string, reiter: string): Container | undefined {
    const oben = document.tabs?.find(t => t.caption === bereich) as Tab | undefined;
    return oben?.containerList?.find(u => u.caption === reiter);
  }

  private karten(zeile: Container): Image[] {
    return (zeile.containerList ?? []).filter(k => k.containerType === 'Image') as Image[];
  }

  /** Kartennummer aus der Beschriftung "HEUTE: ganz - Karte 6". */
  private kartenNummer(karte: Image): number | undefined {
    const treffer = /- Karte (\d+)$/.exec(karte.caption ?? '');
    return treffer ? Number(treffer[1]) : undefined;
  }
}
